import React, { useMemo, useState, useEffect } from 'react';

interface DateTimeInlinePickerProps {
  value?: string; // expected as 'YYYY-MM-DDTHH:MM' (datetime-local format)
  onChange: (value: string) => void;
  minuteStep?: number; // default 5
}

// Helper: format to datetime-local string from a Date (local time)
function toDateTimeLocalString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

// Helper: parse datetime-local string to Date (in local timezone)
function fromDateTimeLocalString(v?: string): Date | null {
  if (!v) return null;
  // Safari compatibility: ensure seconds
  const withSec = v.length === 16 ? v + ':00' : v;
  const d = new Date(withSec);
  return isNaN(d.getTime()) ? null : d;
}

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DateTimeInlinePicker: React.FC<DateTimeInlinePickerProps> = ({ value, onChange, minuteStep = 5 }) => {
  const initial = fromDateTimeLocalString(value) ?? new Date();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(fromDateTimeLocalString(value));
  const [hour, setHour] = useState<number>(initial.getHours());
  const [minute, setMinute] = useState<number>(Math.floor(initial.getMinutes() / minuteStep) * minuteStep);

  useEffect(() => {
    const parsed = fromDateTimeLocalString(value);
    if (parsed) {
      setSelectedDate(parsed);
      setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      setHour(parsed.getHours());
      setMinute(Math.floor(parsed.getMinutes() / minuteStep) * minuteStep);
    }
  }, [value, minuteStep]);

  // Start of today (local), used to disable past dates
  const todayStart = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Build calendar grid for the current month
  const days: { date: Date; inMonth: boolean }[] = useMemo(() => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const startDayIdx = firstDay.getDay(); // 0=Sun .. 6=Sat

    // previous month tail
    const daysPrevMonth = startDayIdx;
    const startDate = new Date(currentMonth);
    startDate.setDate(1 - daysPrevMonth);

    const totalCells = 42; // 6 weeks * 7 days
    const grid: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < totalCells; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      grid.push({
        date: d,
        inMonth: d.getMonth() === currentMonth.getMonth(),
      });
    }
    return grid;
  }, [currentMonth]);

  const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const applyChange = (newDate: Date | null, h: number, m: number) => {
    if (!newDate) return; // require a date before emitting
    const composed = new Date(newDate);
    composed.setHours(h, m, 0, 0);
    onChange(toDateTimeLocalString(composed));
  };

  const handleSelectDay = (d: Date) => {
    const dayStart = new Date(d);
    dayStart.setHours(0, 0, 0, 0);
    if (dayStart < todayStart) return; // disable past dates
    setSelectedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    applyChange(d, hour, minute);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const h = parseInt(e.target.value, 10);
    setHour(h);
    if (selectedDate) applyChange(selectedDate, h, minute);
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const m = parseInt(e.target.value, 10);
    setMinute(m);
    if (selectedDate) applyChange(selectedDate, hour, m);
  };

  const clearSelection = () => {
    setSelectedDate(null);
    const now = new Date();
    setHour(now.getHours());
    setMinute(Math.floor(now.getMinutes() / minuteStep) * minuteStep);
    onChange('');
  };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Build options
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep);

  // Quick presets helpers
  const roundUpToStep = (d: Date, step: number) => {
    const copy = new Date(d);
    const minutes = copy.getMinutes();
    const remainder = minutes % step;
    if (remainder !== 0) {
      copy.setMinutes(minutes + (step - remainder));
    }
    copy.setSeconds(0, 0);
    return copy;
  };

  const setFromDate = (d: Date) => {
    const snapped = roundUpToStep(d, minuteStep);
    setSelectedDate(snapped);
    setCurrentMonth(new Date(snapped.getFullYear(), snapped.getMonth(), 1));
    setHour(snapped.getHours());
    setMinute(snapped.getMinutes());
    onChange(toDateTimeLocalString(snapped));
  };

  const presetNowPlus = (mins: number) => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + mins);
    setFromDate(d);
  };

  const presetTodayAt = (h: number, m: number) => {
    const d = new Date();
    d.setHours(h, m, 0, 0);
    const now = new Date();
    if (d < now) d.setDate(d.getDate() + 1); // if passed, move to tomorrow
    setFromDate(d);
  };

  const presetTomorrowAt = (h: number, m: number) => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(h, m, 0, 0);
    setFromDate(d);
  };

  const presetNextWeekdayAt = (weekday: number, h: number, m: number) => {
    // weekday: 0=Sun ... 6=Sat
    const d = new Date();
    const diff = (7 + weekday - d.getDay()) % 7 || 7; // next occurrence (not today)
    d.setDate(d.getDate() + diff);
    d.setHours(h, m, 0, 0);
    setFromDate(d);
  };

  // If no initial value is provided, preselect current date/time for better UX
  useEffect(() => {
    if (!value) {
      setFromDate(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Calendar */}
          <div>
            {/* Header */}
            <div className="flex items-center justify-between px-1 py-2 border-b">
              <button type="button" onClick={handlePrevMonth} className="px-2 py-1 rounded hover:bg-gray-100" aria-label="Previous month">◀</button>
              <div className="font-medium text-gray-800">
                {currentMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
              </div>
              <button type="button" onClick={handleNextMonth} className="px-2 py-1 rounded hover:bg-gray-100" aria-label="Next month">▶</button>
            </div>

            {/* Weekday labels */}
            <div className="grid grid-cols-7 gap-1 pt-2">
              {weekdayLabels.map((w) => (
                <div key={w} className="text-center text-xs font-medium text-gray-500">{w}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 pt-2">
              {days.map(({ date, inMonth }, idx) => {
                const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
                const dayStart = new Date(date);
                dayStart.setHours(0, 0, 0, 0);
                const isPast = dayStart < todayStart;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectDay(date)}
                    disabled={isPast}
                    className={[
                      'h-10 rounded text-sm flex items-center justify-center transition-colors',
                      inMonth ? (isPast ? 'text-gray-300' : 'text-gray-800') : 'text-gray-400',
                      isSelected ? 'bg-whatsapp-green text-white' : (isPast ? 'bg-gray-50' : 'hover:bg-gray-100'),
                      isPast ? 'cursor-not-allowed' : 'cursor-pointer'
                    ].join(' ')}
                    aria-pressed={isSelected}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Presets and Time */}
          <div className="flex flex-col gap-3">
            {/* Presets */}
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Quick presets</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50" onClick={() => presetNowPlus(15)}>In 15 min</button>
                <button type="button" className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50" onClick={() => presetNowPlus(60)}>In 1 hour</button>
                <button type="button" className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50" onClick={() => presetTodayAt(9, 0)}>Today 09:00</button>
                <button type="button" className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50" onClick={() => presetTomorrowAt(9, 0)}>Tomorrow 09:00</button>
                <button type="button" className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50" onClick={() => presetNextWeekdayAt(1, 9, 0)}>Next Mon 09:00</button>
              </div>
            </div>

            {/* Selected summary */}
            <div className="text-sm text-gray-700">
              {selectedDate ? (
                <span>
                  Selected: {selectedDate.toLocaleDateString()} {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')}
                </span>
              ) : (
                <span>No date selected</span>
              )}
            </div>

            {/* Time selectors - larger UI */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Hour</label>
                <select 
                  value={hour} 
                  onChange={handleHourChange} 
                  className="w-full border rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                >
                  {hourOptions.map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Minute</label>
                <select 
                  value={minute} 
                  onChange={handleMinuteChange} 
                  className="w-full border rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                >
                  {minuteOptions.map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600">
              <div>Timezone: {tz}</div>
              <button type="button" onClick={clearSelection} className="text-red-600 hover:text-red-700">Clear</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateTimeInlinePicker;
